// components/Footer.jsx
import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useEnquiryModal } from "./EnquiryModal";
import { useLeadForm } from "../hooks/useLeadForm";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const { openModal } = useEnquiryModal();
  const navigate = useNavigate();   // initialize navigate

  const {
    phone,
    setPhone,
    dialCode,
    isoCode,
    loading,
    success,
    setSuccess,
    submitLead,
    setDialCode,
    setIsoCode,
    setNumberWithoutCountryCode
  } = useLeadForm();

  const [consentChecked, setConsentChecked] = useState(true);
  const [errors, setErrors] = useState({ name: "", phone: "", consent: "" });

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value.trim();
    const email = e.target.email.value.trim();
    const cleanedPhone = phone ? phone.replace(/\D/g, "") : "";

    const newErrors = { name: "", phone: "", consent: "" };

    // ✅ Name validation
    if (!name) {
      newErrors.name = "Name is required";
    }

    // ✅ Phone validation (strong)
    if (!cleanedPhone) {
      newErrors.phone = "Phone number is required";
    }
    // Allow country code but ensure at least 8 digits total (including code)
    else if (cleanedPhone.length < 8) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // ✅ Consent validation
    if (!consentChecked) {
      newErrors.consent = "You must agree before submitting";
    }

    // Stop form if errors exist
    if (newErrors.name || newErrors.phone || newErrors.consent) {
      setErrors(newErrors);
      return;
    }

    // ✅ No errors — clear errors and submit
    setErrors({ name: "", phone: "", consent: "" });
    const formData = { name, email };

    const result = await submitLead(formData, e?.target);
    if (result) {
      e.target.reset();
      setConsentChecked(false);
      navigate("/thankyou.html");
    }
  };

  return (
    <div>
      <footer id="contact" className="w-full bg-blue-50 p-4 sm:p-8 lg:p-20">
        <div className="container mx-auto">
          {/* Desktop Footer */}
          <div className="hidden lg:flex border-2 rounded border-[#003153] p-16 flex-col items-center">
            <h2 className="text-[#003153] text-2xl font-bold mb-4 ">ENQUIRE NOW</h2>
            <form
              onSubmit={handleFormSubmit}
              className="flex items-end justify-center space-x-4 w-full max-w-5xl"
            >
              {/* Name input */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-[#003153] mb-1 text-sm font-semibold">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[48px] p-3 rounded-lg bg-white border-2 border-[#003153] focus:border-[#003153] outline-none w-full"
                  required
                />
                {errors.name && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.name}</span>
                )}
              </div>

              {/* Phone input */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-[#003153] mb-1 text-sm font-semibold">
                  Phone Number
                </label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(value.replace(country.dialCode, ""));
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full"
                  inputClass="!w-full !h-[48px] !pl-12 !pr-3 !rounded-lg !bg-white !text-black !border-2 !border-[#003153] focus:!border-[#003153] !outline-none placeholder:!text-gray-400"
                  buttonClass="!h-[48px] !flex !items-center !justify-center !bg-gray-300 !border-none !rounded-l-lg "
                  dropdownClass="!bg-white !text-black"
                  searchClass="!bg-white !text-black"
                  enableSearch={true}
                  placeholder="Enter phone number"
                  required
                />
                {errors.phone && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="flex-1 flex flex-col">
                <label className="text-[#003153] mb-1 text-sm font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[48px] p-3 rounded-lg bg-white border-2 border-[#003153] focus:border-[#003153] outline-none w-full"
                // required
                />
              </div>

              <button
                type="submit"
                className="bg-[#003153] text-[#ffffff] px-8 py-2.5 rounded-lg font-bold hover:text-[#003153] border-2 border-[#003153] hover:bg-white transition-colors h-fit"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>

            <div className="relative flex items-start space-x-2 text-gray-200 font-medium text-sm mt-4 text-center max-w-2xl">
              <input
                type="checkbox"
                id="consent"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#003153] cursor-pointer"
                required
              />
              <label
                htmlFor="consent"
                className="text-xs text-[#003153] leading-relaxed cursor-pointer"
              >
                I consent to the processing of provided data according to the{" "}
                <Link
                  to="/privacy-policy"
                  // onClick={onClose}
                  className="underline hover:text-bold"
                >
                  Privacy Policy | Terms & Conditions
                </Link>{" "}
                . I authorize Nova Capital Real Estate and its representatives
                to call, SMS, email, or WhatsApp me about its products and
                offers. This consent overrides any registration for DNC / DNCR.
              </label>
              {errors.consent && (
                <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.consent}</span>
              )}
            </div>
          </div>

          {/* Mobile/Tablet Footer */}
          <div className="lg:hidden mb-5 flex flex-col items-center space-y-4">
            <h2 className="text-[#003153] mt-5 text-2xl font-bold">ENQUIRE NOW</h2>
            <form onSubmit={handleFormSubmit} className="w-full space-y-4">
              <div className="flex flex-col">
                <label className="text-[#003153] mb-1 text-sm font-semibold">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border-2 border-[#003153] focus:border-[#003153] outline-none"
                  required
                />
                {errors.name && (
                  <span className="text-black text-sm mt-1">{errors.name}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-[#003153] mb-1 text-sm font-semibold">Phone Number</label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(value.replace(country.dialCode, ""));
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full "   // ✅ relative so dropdown positions properly
                  // inputClass="!w-full !h-[48px] !pl-12 !pr-3 !rounded-lg !bg-white !text-black !border !border-gray-300 focus:!border-[#997736] !outline-none placeholder:!text-gray-400"
                  // buttonClass="!h-[48px] !flex !items-center !justify-center !bg-gray-200 !border-none !rounded-l-lg !px-1"
                  inputClass="!w-full !bg-white !text-black !py-3 !pl-12 !pr-3 !rounded-md !border-2 !border-[#003153] focus:!border-[#003153] !outline-none"
                  buttonClass="!bg-gray-800 !border-none !rounded-l-md !bg-gray-200"
                  dropdownClass="!bg-white !text-black "   // ✅ keep dropdown above !z-[9999] !absolute
                  searchClass="!bg-white !text-black"
                  enableSearch={true}
                  required
                // placeholder="Enter phone number"
                // dropdownStyle={{ position: "absolute", zIndex: 9999 }}  // ✅ important
                />
                {errors.phone && (
                  <span className="text-black text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-[#003153] mb-1 text-sm font-semibold">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border-2 border-[#003153] focus:border-[#003153] outline-none"
                // required
                />
              </div>

              <div className=" flex items-start space-x-1 text-gray-200 font-medium text-xs text-center max-w-2xl mt-2">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#003153] cursor-pointer"
                  required
                />
                <label
                  htmlFor="consent"
                  className="text-xs text-[#003153] leading-relaxed cursor-pointer"
                >
                  I consent to the processing of provided data according to the{" "}
                  <Link
                    to="/privacy-policy"
                    // onClick={onClose}
                    className="underline hover:text-semibold"
                  >
                    Privacy Policy | Terms & Conditions
                  </Link>{" "}
                  . I authorize Nova Capital Real Estate and its representatives
                  to call, SMS, email, or WhatsApp me about its products and
                  offers. This consent overrides any registration for DNC / DNCR.
                </label>
                {errors.consent && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.consent}</span>
                )}
              </div>

              <button
                type="submit"
                className="bg-[#003153] w-full text-lg text-[#ffffff] border-2 border-[#003153] px-8 py-2.5 rounded-lg font-bold hover:text-[#003153] hover:bg-white transition-colors h-fit"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>
          </div>
        </div>
      </footer>

      {/* Fixed Mobile Footer Buttons */}
      {/* <div className="fixed bottom-0 left-0 w-full bg-[#003153] shadow-md lg:hidden z-50">
        <div className="justify-around w-full p-2 space-x-2">
          <button onClick={openModal} className="flex-1 flex flex-col items-center p-2 rounded-lg bg-[#b38e5d] text-white hover:bg-[#a07e4d] transition-colors">
            <i className="fas fa-phone-alt text-lg transform rotate-90"></i>
            <span className="text-sm mt-1">CALL</span>
          </button>
          <button onClick={openModal} className="flex-1 text-center justify-center flex gap-2 w-full items-center p-2 rounded-lg font-semibold bg-[#f6f8f2] text-[#003153] hover:text-white hover:bg-[#003153] transition-colors">
            <i className="fas fa-envelope text-lg"></i>
            <span className="text-md mt-1">ENQUIRY</span>
          </button>
          <button onClick={openModal} className="flex-1 flex flex-col items-center p-2 rounded-lg bg-[#b38e5d] text-white hover:bg-[#a07e4d] transition-colors">
            <i className="fab fa-whatsapp text-lg"></i>
            <span className="text-sm mt-1">WHATSAPP</span>
          </button>
        </div>
      </div> */}

    </div>
  );
};

export default Footer;
